import { useEffect, useState } from 'react';
import { Download, AlertTriangle, X } from 'lucide-react';
import api from '@/lib/api';
import { BANNER_KEY, BANNER_ROLES, SELF_UPDATING_FROM, bannerFor, latestPublished } from '@/lib/footagePull';

/**
 * Getting editors onto the Footage Pull that brings the prepared project (Ryan, 2026-10-09: "old footage pull should not
 * work. can we add a button or something to make it easier for them to update"). The portal refuses an editor's older
 * copy (backend app/footage_app_version.py); these put the new one a click away.
 */

/** The published builds as download buttons (GET /api/tools/footage_pull/version, as the Editor Guide reads them). */
export function AppDownloads({ testid = 'footage-pull-update-downloads' }) {
  const [builds, setBuilds] = useState(null);
  useEffect(() => {
    let gone = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tools/footage_pull/version`)
      .then(r => r.json()).then(d => { if (!gone) setBuilds(d?.builds || {}); })
      .catch(() => { if (!gone) setBuilds({}); });
    return () => { gone = true; };
  }, []);
  const list = ['mac-universal', 'windows-x64'].filter(k => builds?.[k]);
  if (!builds) return <span className="text-[11px] text-slate-500">Finding the downloads…</span>;
  // None listed (a portal without the packaged app, or the list could not be read): the Editor Guide's own download spot
  if (!list.length) {
    return (
      <a href="/editor/guide#footage-pull" target="_blank" rel="noreferrer" data-testid={`${testid}-guide`}
         className="tap-auto inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-obsidian bg-gold-leaf hover:opacity-90 rounded-full px-3.5 py-1.5">
        <Download className="w-3.5 h-3.5" /> Open the Footage Pull download page
      </a>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {list.map(k => (
        <a key={k} href={`${process.env.REACT_APP_BACKEND_URL}${builds[k].url}`} data-testid={`${testid}-${k}`}
           className="tap-auto inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-obsidian bg-gold-leaf hover:opacity-90 rounded-full px-3.5 py-1.5">
          <Download className="w-3.5 h-3.5" /> Footage Pull {builds[k].version} for {builds[k].label}
        </a>
      ))}
    </div>
  );
}

/** Above an editor's Download footage button, when the copy they last used is too old (GET /footage-pull/app-status). */
export function FootagePullUpdateBox() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let gone = false;
    api.get('/footage-pull/app-status').then(r => { if (!gone) setSt(r.data); }).catch(() => {});
    return () => { gone = true; };
  }, []);
  if (!st?.needs_update) return null;
  return (
    <div data-testid="footage-pull-update" className="mb-3 rounded-xl border px-4 py-3 space-y-2.5"
         style={{ borderColor: 'rgba(220, 38, 38, 0.55)', background: 'rgba(220, 38, 38, 0.07)' }}>
      <p className="flex items-start gap-2.5 text-[12.5px] text-zinc-100 leading-relaxed">
        {/* red in both themes, set here (the popup's update box, PreparedProjectsAnnouncement) */}
        <span className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center" style={{ background: '#dc2626' }} aria-hidden="true" data-ug="badge">
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#ffffff' }} strokeWidth={2.5} />
        </span>
        <span>
          <span className="font-semibold">Update Footage Pull to {st.min_version} to download footage.</span>{' '}
          Your copy is {st.your_version}, which can&rsquo;t download any more: the new one sets up the project prepared for
          you on any drive. If your Footage Pull has an Update button, press it. Otherwise download it here: on a Mac, drag it into Applications and choose Replace; on Windows, unzip it in place of the old folder.
          Your login and half-finished downloads carry over.
        </span>
      </p>
      <AppDownloads />
    </div>
  );
}

/**
 * The editors' "a new Footage Pull is out" banner, at the top of every page (Ryan, 2026-10-10: "maybe have a
 * dismissable banner saying new footage pull 2.13 update available on the editor side"). Shown while the copy the
 * editor last used is older than the newest one published; closing it hides it for that version only, in this
 * browser (a newer version shows it again). From 2.13 the app updates itself, so the banner points at its Update
 * button; an older copy gets the downloads, once. lib/footagePull.js bannerFor decides. Admin and staff get it too
 * (BANNER_ROLES, 2026-10-22), without the Editor Guide link: that page is for editors only.
 */
export function FootagePullNewVersionBanner({ role }) {
  const [info, setInfo] = useState(null);           // { yours, builds }
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(BANNER_KEY); } catch { return null; }
  });
  useEffect(() => {
    if (!BANNER_ROLES.includes(role)) return undefined;
    let gone = false;
    Promise.all([
      api.get('/footage-pull/app-status').then(r => r.data?.your_version || null).catch(() => null),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tools/footage_pull/version`).then(r => r.json()).then(d => d?.builds || {}).catch(() => ({})),
    ]).then(([yours, builds]) => { if (!gone) setInfo({ yours, builds }); });
    return () => { gone = true; };
  }, [role]);
  const show = info && bannerFor({ role, yours: info.yours, latest: latestPublished(info.builds), dismissed });
  if (!show) return null;
  const close = () => {
    try { localStorage.setItem(BANNER_KEY, show.latest); } catch { /* private window: it closes for this visit */ }
    setDismissed(show.latest);
  };
  const downloads = ['mac-universal', 'windows-x64'].filter(k => info.builds?.[k]);
  return (
    <div className="shrink-0 bg-oxidized-teal/10 border-b border-oxidized-teal/20 px-4 py-2 flex items-start sm:items-center justify-between gap-3"
         data-testid="footage-pull-new-version-banner">
      <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <div className="flex items-start sm:items-center gap-2 min-w-0">
          <Download className="w-3.5 h-3.5 text-oxidized-teal shrink-0 mt-0.5 sm:mt-0" strokeWidth={2} />
          <span className="font-sans text-[11px] text-oxidized-teal leading-snug" data-testid="footage-pull-new-version-text">
            <strong className="font-semibold">Footage Pull {show.latest} is available.</strong>{' '}
            {show.hasButton
              ? <>Open Footage Pull and press <strong className="font-semibold">Update</strong> at the top of its window.</>
              : show.latestUpdatesItself
                ? <>You have {show.yours}. Download it once; from {SELF_UPDATING_FROM} on, it updates itself with one click.</>
                : <>You have {show.yours}. Download the new one below.</>}
          </span>
        </div>
        {!show.hasButton && (
          <span className="flex items-center gap-2 pl-[22px] sm:pl-0 font-sans text-[11px] shrink-0">
            {downloads.map(k => (
              <a key={k} href={`${process.env.REACT_APP_BACKEND_URL}${info.builds[k].url}`} data-testid={`footage-pull-banner-download-${k}`}
                 className="tap-auto font-semibold text-oxidized-teal underline underline-offset-2">{info.builds[k].label}</a>
            ))}
            {role === 'editor' && (
              <a href="/editor/guide#footage-pull" data-testid="footage-pull-banner-guide"
                 className="tap-auto text-oxidized-teal underline underline-offset-2">How to update</a>
            )}
          </span>
        )}
      </div>
      <button onClick={close} data-testid="dismiss-footage-pull-banner-btn" aria-label="Dismiss"
        className="tap-auto shrink-0 text-oxidized-teal/70 hover:text-oxidized-teal transition-colors p-0.5">
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
